/**
 * Default values for ApprovalModalV3 components
 *
 * Centralizes all magic values and default configurations
 * to ensure consistency across components.
 */

// =============================================================================
// Style Defaults
// =============================================================================

export const DEFAULT_TEXT_COLOR = '#FFFFFF';
export const DEFAULT_BACKGROUND_COLOR = '#000000';
export const DEFAULT_TEXT_HAS_BACKGROUND = false;

export const DEFAULT_STYLE = {
  textColor: DEFAULT_TEXT_COLOR,
  backgroundColor: DEFAULT_BACKGROUND_COLOR,
  textHasBackground: DEFAULT_TEXT_HAS_BACKGROUND,
};

// =============================================================================
// Position Defaults
// =============================================================================

export const DEFAULT_TEXT_POSITION = { x: 540, y: 1700 };
export const DEFAULT_TEXT_ALIGNMENT = 'left';
export const DEFAULT_TEXT_AREA_WIDTH = 800;
export const DEFAULT_TEXT_AREA_HEIGHT = 400;
export const DEFAULT_FONT_SIZE = 48;

// =============================================================================
// Font Defaults
// =============================================================================

export const DEFAULT_FONT = 'Classic';

// =============================================================================
// Background Configuration Defaults
// =============================================================================

export const DEFAULT_BACKGROUND_MODE = 'solid';
export const DEFAULT_SOLID_BACKGROUND_COLOR = '#1E1E1E';
export const DEFAULT_GRADIENT_TYPE = 'linear';
export const DEFAULT_GRADIENT_COLORS = ['#FF0080', '#7928CA'];
export const DEFAULT_GRADIENT_ANGLE = 45;

export const DEFAULT_BACKGROUND_CONFIG = {
  backgroundColor: DEFAULT_SOLID_BACKGROUND_COLOR,
  gradientType: DEFAULT_GRADIENT_TYPE,
  gradientColors: DEFAULT_GRADIENT_COLORS,
  gradientAngle: DEFAULT_GRADIENT_ANGLE,
};

// =============================================================================
// Product/E-Commerce Defaults
// =============================================================================

export const DEFAULT_CAPTION_COLOR = '#ffffff';
export const DEFAULT_TEMPLATE_ID = 'MinimalistReveal';

export const DEFAULT_VISUAL_EFFECTS = {
  sparkles: true,
  floatingShapes: true,
  bokeh: true,
  ambientGlow: true,
  priceShimmer: true,
  pulsingGlow: false,
};

export const DEFAULT_BACKGROUND_TYPE = 'aurora';
export const DEFAULT_BACKGROUND_PALETTE = 'aurora';
export const DEFAULT_TEXT_ANIMATION = 'typewriter';
export const DEFAULT_CAPTION_FONT = 'Modern';

// =============================================================================
// Timing Defaults (in milliseconds)
// =============================================================================

export const DEBOUNCE_CAPTION_PREVIEW_MS = 100;
export const DEBOUNCE_GIF_SAVE_MS = 1000;
export const SANITIZATION_MAX_LENGTH = 500;

// =============================================================================
// Caption Color Presets
// =============================================================================

export const CAPTION_COLOR_PRESETS = [
  { color: '#ffffff', name: 'White' },
  { color: '#000000', name: 'Black' },
  { color: '#FFD700', name: 'Gold' },
  { color: '#4ECDC4', name: 'Teal' },
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Create initial style from item data
 */
export function getInitialStyle(item) {
  return {
    textColor: item?.text_color_hex || DEFAULT_TEXT_COLOR,
    backgroundColor: item?.text_background_color_hex || DEFAULT_BACKGROUND_COLOR,
    textHasBackground: item?.text_has_background || DEFAULT_TEXT_HAS_BACKGROUND,
  };
}

/**
 * Create initial position from item data
 */
export function getInitialPosition(item) {
  return item?.text_position || item?.caption?.text_position || DEFAULT_TEXT_POSITION;
}

/**
 * Create initial background config from item data
 */
export function getInitialBackgroundConfig(item) {
  const metadata = item?.background_metadata || {};
  return {
    mode: metadata.background_type || DEFAULT_BACKGROUND_MODE,
    backgroundColor: metadata.background_color || DEFAULT_SOLID_BACKGROUND_COLOR,
    gradientType: metadata.gradient_type || DEFAULT_GRADIENT_TYPE,
    gradientColors: metadata.gradient_colors || DEFAULT_GRADIENT_COLORS,
    gradientAngle: metadata.gradient_angle || DEFAULT_GRADIENT_ANGLE,
  };
}

/**
 * Create initial visual effects from item data
 */
export function getInitialVisualEffects(item) {
  return item?.effects || item?.llm_suggestion?.effects || DEFAULT_VISUAL_EFFECTS;
}
